import { Component, inject, TemplateRef, ViewChild, OnDestroy, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import {
  ColumnMode,
  DatatableComponent,
  SelectionType,
  TableColumn,
} from '@siemens/ngx-datatable';
import { StatusType } from '@siemens/element-ng/common';
import { SI_DATATABLE_CONFIG } from '@siemens/element-ng/datatable';
import { Link } from '@siemens/element-ng/link';
import { ModalRef, SiModalService } from '@siemens/element-ng/modal';
import { SelectOption } from '@siemens/element-ng/select';
import { SidePanelMode, SidePanelSize } from '@siemens/element-ng/side-panel';
import { SiToastNotificationService } from '@siemens/element-ng/toast-notification';
import { CONSTANTS, NOTIFICATIONS } from 'src/app/constants/constant';
import { Project } from 'src/app/models/project';
import { User } from 'src/app/models/user';
import { ConfigService } from 'src/app/services/config.service';
import { ProjectService } from 'src/app/services/project.service';
import { isPersonalProject } from 'src/app/helpers/personal-project';
import { UserService } from 'src/app/services/user.service';
import { CreateProjectComponent } from '../create-project/create-project.component';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus, EventType, EventMessage } from '@azure/msal-browser';
import { Subject, filter, takeUntil } from 'rxjs';
import { canEditProject } from 'src/app/helpers/project-role';

type ToastStateName = StatusType;

@Component({
  selector: 'app-uss-dashboard-landing',
  templateUrl: './uss-dashboard-landing.component.html',
  styleUrl: './uss-dashboard-landing.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class UssDashboardLandingComponent implements OnDestroy {
  @ViewChild(DatatableComponent) table!: DatatableComponent;
  @ViewChild('contextCellTempl', { static: true })
  contextCellTempl!: TemplateRef<any>;

  private ref?: ModalRef<unknown>;
  private modalService = inject(SiModalService);

  collapsed = true;
  columns!: TableColumn[];
  ColumnMode = ColumnMode;
  constants = CONSTANTS;
  currentUser: User | undefined;
  folderList: SelectOption<string>[] = [];
  isLoading = false;
  isSelectedProjectAPersonalProject = false;
  hideErrorPanel = true;
  hideProjectsPanel = true;
  hideWelcomeNoProjectsPanel = true;
  mode: SidePanelMode = 'scroll';
  notificationConstants = NOTIFICATIONS;
  pageNumber = 0;
  pageSize = 20; // Updated to show 20 projects per page
  projects: Project[] = [];
  selectedProject: Project | undefined;
  selectedProjectParentId = '';
  selectionType = SelectionType.single;
  sidePanelSubscriptionClusterInfo: any = {};
  sidePanelUserInfo: any = {};
  size: SidePanelSize = 'wide';
  tableConfig = SI_DATATABLE_CONFIG;
  tempProjects: Project[] = [];
  private destroying$ = new Subject<void>();
  private dataLoaded = false;
  isSearching = false;
  currentSearchTerm = '';
  isHoveringClearBtn = false;
  
  // Cache for performance optimization
  private _cachedFlattenedProjects: any[] = [];
  private _lastProjectsHash = '';
  
  // Track loading state for individual rows
  private loadingChildrenMap = new Set<string>();
  
  // Cache for project details to avoid redundant API calls
  private _lastLoadedProjectId: string | null = null;
  private _projectDetailsCache: Map<string, any> = new Map();
  private _projectUsersCache: Map<string, any> = new Map();
  isLoadingSubscriptions = false;
  isLoadingClusters = false;
  isLoadingUsers = false;

  constructor(
    private userService: UserService,
    private projectService: ProjectService,
    private configService: ConfigService,
    private toastNotificationService: SiToastNotificationService,
    private msalService: MsalService,
    private msalBroadcast: MsalBroadcastService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.initDashboardTableColumns();
    // Listen for end of any interactive auth
    this.msalBroadcast.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this.destroying$)
      )
      .subscribe(() => {
       
        const accounts = this.msalService.instance.getAllAccounts();
        if (accounts.length > 0 && !this.dataLoaded) {
       
          this.currentUser = this.configService.currentUser; // remains whatever your app sets later
          this.loadInitialData();
        }
      });

    // Log every inProgress status transition for diagnostics
    this.msalBroadcast.inProgress$
      .pipe(takeUntil(this.destroying$))
      .subscribe((status) => {
       
      });

    // Explicitly react to LOGIN_SUCCESS (more deterministic than relying solely on InteractionStatus)
    this.msalBroadcast.msalSubject$
      .pipe(
        filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
        takeUntil(this.destroying$)
      )
      .subscribe((msg) => {
       
     //   const accounts = this.msalService.instance.getAllAccounts();
        this.loadInitialData();
      });

    // Fallback timer in case events missed due to race conditions
    setTimeout(() => {
      const accounts = this.msalService.instance.getAllAccounts();
      if (accounts.length > 0 && !this.dataLoaded) {
       
        this.loadInitialData();
      } else {
      
      }
    }, 2000);
    // Second fallback if first missed
    setTimeout(() => {
      const accounts = this.msalService.instance.getAllAccounts();
      if (accounts.length > 0 && !this.dataLoaded) {
     
        this.loadInitialData();
      }
    }, 5000);
    // In case guard already finished before subscription
    const accounts = this.msalService.instance.getAllAccounts();
    if (accounts.length > 0) {
   
      if (!this.dataLoaded) {
        this.loadInitialData();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroying$.next();
    this.destroying$.complete();
  }

  private loadInitialData(): void {
    if (this.dataLoaded) return;
    this.dataLoaded = true;
 
    this.getProjects();
  }

  private initDashboardTableColumns(): void {
    this.columns = [
      {
        prop: 'name',
        name: 'Project Name',
        minWidth: 100,
        resizeable: true,
        canAutoResize: true,
      },
      // {
      //   prop: 'businessUnit',
      //   name: 'Business Unit',
      //   minWidth: 40,
      //   resizeable: true,
      //   canAutoResize: true,
      // },
      {
        prop: 'numSubscriptions',
        name: 'Subscriptions',
        minWidth: 10,
        cellClass: 'text-align-center-cell',
        resizeable: true,
        canAutoResize: true,
      },
      {
        prop: 'numKubernetesClusters',
        name: 'Kubernetes Clusters',
        minWidth: 10,
        cellClass: 'text-align-center-cell',
        resizeable: true,
        canAutoResize: true,
      },
      {
        prop: 'isFolder',
        name: '',
        minWidth: 10,
        resizeable: false,
        canAutoResize: false,
      },
    ];
  }

  private getProjects() {
    this.isLoading = true;
    // Clear caches when reloading projects to ensure fresh data
    this._projectDetailsCache.clear();
    this._projectUsersCache.clear();
    this._lastLoadedProjectId = null;
    
    // Load only owned (root/parent) projects initially
    this.projectService.getOwnedProjects().subscribe({
      next: (projects) => {
        if (projects !== null && projects.length > 0) {
          // Map projects and use hasChildren property to determine tree status
          this.projects = projects.map((p: any) => ({
            ...p,
            ancestorId: null, // Root projects have no ancestor
            treeStatus: p.hasChildren ? 'collapsed' : 'disabled',
            childrenLoaded: false, // Track if children have been loaded
            children: [], // Store loaded children
            level: 0 // Root level
          }));
          
          // Sort projects: folders first (isFolder=true), then regular projects
          this.projects = this.projects.sort((a: any, b: any) => {
            if (a.isFolder !== b.isFolder) {
              return a.isFolder ? -1 : 1;
            }
            return a.name.localeCompare(b.name);
          });
          
          this.tempProjects = [...this.projects];
          this.hideWelcomeNoProjectsPanel = true;
          this.hideProjectsPanel = false;
          this.setFolderProjects();
        } else {
          this.hideWelcomeNoProjectsPanel = false;
        }
        this.isLoading = false;
        this.cdr.markForCheck(); // Trigger change detection
      },
      error: (err) => {
        this.showToast(
          this.notificationConstants.warning as ToastStateName,
          this.notificationConstants.error,
          err.error
        );
        this.isLoading = false;
        this.hideErrorPanel = false;
      },
    });
  }

  private setFolderProjects() {
    // Reset the folder list to prevent duplicates
    this.folderList = [{ type: 'option', value: 'Root', label: 'Root' }];
    
    // Add all unique folders from the current projects list.
    // Security: Only show folders where current user can edit.
    const uniqueFolders = new Set<string>();
    this.projects.forEach((proj) => {
      if (proj.isFolder && !uniqueFolders.has(proj.id) && canEditProject(proj.projectRole)) {
        uniqueFolders.add(proj.id);
        this.folderList.push({ type: 'option', value: proj.id, label: proj.name });
      }
    });
  }

  public updateFilter(event: any) {
    // Only trigger search on Enter key press
    if (event.key === 'Enter') {
      const val = event.target.value.trim();
      this.performSearch(val);
    }
  }

  public clearSearch(): void {
    // Clear the search input
    const searchInput = document.querySelector('input[type="text"]') as HTMLInputElement;
    if (searchInput) {
      searchInput.value = '';
    }
    
    // Reset search state
    this.currentSearchTerm = '';
    this.isSearching = false;
    
    // Force reload of original projects data
    this.dataLoaded = false; // Reset data loaded flag to force fresh load
    this.projects = [];
    this.tempProjects = [];
    this.pageNumber = 0; // Reset to first page
    
    // Show loading state and reload all data
    this.isLoading = true;
    this.loadInitialData();

  }

  private performSearch(searchTerm: string): void {
    this.currentSearchTerm = searchTerm.trim();
    
    if (!this.currentSearchTerm) {
      // If search is empty, reload original projects
      this.loadInitialData();
      return;
    }

    // Clear caches when searching to ensure fresh data
    this._projectDetailsCache.clear();
    this._projectUsersCache.clear();
    this._lastLoadedProjectId = null;

    this.isSearching = true;
    this.projectService.searchProjects(this.currentSearchTerm).subscribe({
      next: (projects: Project[]) => {
        this.projects = projects.map((p: any) => ({
          ...p,
          treeStatus: 'collapsed',
        }));
        this.tempProjects = [...this.projects];
        this.isSearching = false;
        
        // When searching, always keep projects panel visible (even if no results)
        // The empty state in the datatable will show "No projects to display"
        this.hideProjectsPanel = false;
        this.hideWelcomeNoProjectsPanel = true;
        
        this.cdr.markForCheck(); // Trigger change detection
        
        // Reset to first page when search results change
        this.table.offset = 0;
      },
      error: (err) => {
        console.error('Search failed:', err);
        this.isSearching = false;
        this.toastNotificationService.showToastNotification({
          state: this.notificationConstants.warning as ToastStateName,
          title: 'Search Error',
          message: 'Failed to search projects'
        });
      }
    });
  }

  private setProject(projects: Project[]) {
    this.selectedProject = projects[0];
    this.collapsed = false;
    this.isSelectedProjectAPersonalProject = isPersonalProject(this.selectedProject);
    
    const projectId = this.selectedProject!.id;
    
    // Check if this is the same project we already loaded
    if (this._lastLoadedProjectId === projectId) {
      // Data is already loaded, just reopen the panel
      return;
    }
    
    // New project selected, load its data

    this._lastLoadedProjectId = projectId;
    
    this.selectedProjectParentId = 'None';
    if (
      this.selectedProject.ancestorProjects !== undefined &&
      this.selectedProject.ancestorProjects?.length > 0
    ) {
      this.selectedProjectParentId =
        this.selectedProject.ancestorProjects[0].id +
        ' - ' +
        this.selectedProject.ancestorProjects[0].name;
    }

    this.getProject(projectId);
    this.getUsers(projectId);
  }

  private getProject(id: string) {
    // Check cache first
    if (this._projectDetailsCache.has(id)) {
      this.isLoadingSubscriptions = false;
      this.isLoadingClusters = false;
      const cachedData = this._projectDetailsCache.get(id);
      this.selectedProject!.cloudAccountLinks = cachedData.subscriptions;
      this.selectedProject!.kubernetesClusters = cachedData.clusters;
      this.sidePanelSubscriptionClusterInfo = {
        projectSubscriptionRows: cachedData.subscriptions,
        projectTempSubRows: cachedData.subscriptions,
        clusterSubscriptionRows: cachedData.clusters,
        clusterTempSubRows: cachedData.clusters,
        totalSubscriptions: cachedData.totalSubscriptions,
        totalClusters: cachedData.totalClusters,
      };
      return;
    }

    // Skip loading subscriptions and clusters if this is a folder
    if (this.selectedProject?.isFolder) {
      this.isLoadingSubscriptions = false;
      this.isLoadingClusters = false;
      this.sidePanelSubscriptionClusterInfo = {
        projectSubscriptionRows: [],
        projectTempSubRows: [],
        clusterSubscriptionRows: [],
        clusterTempSubRows: [],
        totalSubscriptions: 0,
        totalClusters: 0,
      };
      return;
    }

    // Fetch subscriptions (first 10 items)
    this.isLoadingSubscriptions = true;
    this.projectService.getProjectSubscriptions(id, 0, 10).subscribe({
      next: (response) => {
        console.log('Subscriptions response:', response);
        this.isLoadingSubscriptions = false;
        const subscriptions = response.items || [];
        
        // Update selectedProject with the loaded subscriptions so edit modal can reuse them
        this.selectedProject!.cloudAccountLinks = subscriptions;
        
        // Update cache
        const cachedData = this._projectDetailsCache.get(id) || {};
        cachedData.subscriptions = subscriptions;
        cachedData.totalSubscriptions = response.totalCount || 0;
        this._projectDetailsCache.set(id, cachedData);
        
        // Initialize or update side panel info
        if (!this.sidePanelSubscriptionClusterInfo) {
          this.sidePanelSubscriptionClusterInfo = {
            projectSubscriptionRows: [],
            projectTempSubRows: [],
            clusterSubscriptionRows: [],
            clusterTempSubRows: [],
          };
        }
        
        this.sidePanelSubscriptionClusterInfo.projectSubscriptionRows = subscriptions;
        this.sidePanelSubscriptionClusterInfo.projectTempSubRows = subscriptions;
        this.sidePanelSubscriptionClusterInfo.totalSubscriptions = response.totalCount || 0;
      },
      error: (err) => {
        console.error('Subscriptions error:', err);
        this.isLoadingSubscriptions = false;
        this.showToast(
          this.notificationConstants.warning as ToastStateName,
          this.notificationConstants.error,
          'Failed to load subscriptions'
        );
      },
    });

    // Fetch clusters (first 10 items)
    this.isLoadingClusters = true;
    this.projectService.getProjectClusters(id, 0, 10).subscribe({
      next: (response) => {
        console.log('Clusters response:', response);
        this.isLoadingClusters = false;
        const clusters = response.items || [];
        
        // Update selectedProject with the loaded clusters so edit modal can reuse them
        this.selectedProject!.kubernetesClusters = clusters;
        
        // Update cache
        const cachedData = this._projectDetailsCache.get(id) || {};
        cachedData.clusters = clusters;
        cachedData.totalClusters = response.totalCount || 0;
        this._projectDetailsCache.set(id, cachedData);
        
        // Initialize or update side panel info
        if (!this.sidePanelSubscriptionClusterInfo) {
          this.sidePanelSubscriptionClusterInfo = {
            projectSubscriptionRows: [],
            projectTempSubRows: [],
            clusterSubscriptionRows: [],
            clusterTempSubRows: [],
          };
        }
        
        this.sidePanelSubscriptionClusterInfo.clusterSubscriptionRows = clusters;
        this.sidePanelSubscriptionClusterInfo.clusterTempSubRows = clusters;
        this.sidePanelSubscriptionClusterInfo.totalClusters = response.totalCount || 0;
      },
      error: (err) => {
        console.error('Clusters error:', err);
        this.isLoadingClusters = false;
        this.showToast(
          this.notificationConstants.warning as ToastStateName,
          this.notificationConstants.error,
          'Failed to load clusters'
        );
      },
    });
  }

  private getUsers(projectId: string) {
    // Check cache first
    if (this._projectUsersCache.has(projectId)) {
      this.isLoadingUsers = false;
      const cachedUserInfo = this._projectUsersCache.get(projectId);
      this.sidePanelUserInfo = cachedUserInfo;
      if (this.selectedProject?.id === projectId) {
        this.selectedProject = {
          ...this.selectedProject,
          entraGroups:
            cachedUserInfo.entraGroups ?? this.selectedProject.entraGroups,
          entraGroupIds:
            cachedUserInfo.entraGroupIds ?? this.selectedProject.entraGroupIds,
        };
      }
      this.cdr.markForCheck();
      return;
    }

    // Fetch from API and cache
    this.isLoadingUsers = true;
    this.userService.getProjectUsers(projectId).subscribe({
      next: (data: any) => {
        let projectLastAccessReviewedAt;
        if (data.lastAccessReviewAt === null) {
          projectLastAccessReviewedAt = 'No Yet Reviewed';
        } else {
          projectLastAccessReviewedAt = new Date(
            parseInt(data.lastAccessReviewAt) * 1000
          ).toDateString();
        }
        const userInfo = {
          userRows: data.assignedUsersList,
          tempUserRows: data.assignedUsersList,
          projectLastAccessReviewedAt: projectLastAccessReviewedAt,
          projectLastAccessReviewedBy: data.lastAccessReviewBy,
          entraGroups: data.entraGroups,
          entraGroupIds: data.entraGroupIds,
        };

        if (this.selectedProject?.id === projectId) {
          this.selectedProject = {
            ...this.selectedProject,
            entraGroups:
              data.entraGroups ?? this.selectedProject.entraGroups,
            entraGroupIds:
              data.entraGroupIds ?? this.selectedProject.entraGroupIds,
          };
        }
        
        // Cache the user data
        this._projectUsersCache.set(projectId, userInfo);
        this.sidePanelUserInfo = userInfo;
        this.isLoadingUsers = false;
        this.cdr.markForCheck();
      },
      error: (err) => {
        this.isLoadingUsers = false;
        this.showToast(
          this.notificationConstants.danger as ToastStateName,
          this.notificationConstants.error,
          this.notificationConstants.fetchUsersError
        );
        this.cdr.markForCheck();
      },
    });
  }

  private toggle() {
    this.collapsed = !this.collapsed;
  }

  private applyProjectUpdate(updatedProject: any) {
    if (!updatedProject?.id) {
      return;
    }

    this.projects = this.projects.map((project: any) =>
      project.id === updatedProject.id
        ? {
            ...project,
            ...updatedProject,
            subscriptionCount:
              updatedProject.subscriptionCount ?? project.subscriptionCount ?? 0,
            kubernetesClusterCount:
              updatedProject.kubernetesClusterCount ??
              project.kubernetesClusterCount ??
              0,
          }
        : project
    );

    if (this.selectedProject?.id === updatedProject.id) {
      this.selectedProject = {
        ...this.selectedProject,
        ...updatedProject,
      };
    }

    this.updateProjectsDisplay();
  }

  public sidePanelAction(event: any) {
    this.toggle();
    if (event?.changed && event?.changeType === 'updated' && event?.project) {
      this.applyProjectUpdate(event.project);
      return;
    }

    // Only reload projects if changes were made (project created/edited/deleted)
    if (event && event.changed) {
      this.getProjects();
    }
  }

  public createProject() {
    this.openModal(CreateProjectComponent, 'modal-lg');
  }

  private openModal(template: any, modalClass?: string) {
    this.ref?.hide();
    this.ref = this.modalService.show(template, {
      ignoreBackdropClick: true,
      keyboard: true,
      animated: true,
      class: modalClass,
      ariaLabelledBy: 'create -project-modal',
      inputValues: { folderList: this.folderList },
    });

    const createProject = this.ref.content as CreateProjectComponent;
    createProject.onClose.subscribe((response: any) => {
      if (response) {
        this.ref?.hide();
      }
    });

    createProject.onSave.subscribe((response: any) => {
      if (response) {
        this.ref?.hide();
        this.getProjects();
      }
    });
  }

  public onTreeAction(event: any) {
    const row = event.row;
    
    if (row.treeStatus === 'collapsed') {
      // Expanding - load children if not already loaded
      if (!row.childrenLoaded) {
        this.loadProjectChildren(row);
      } else {
        // Children already loaded, just expand
        row.treeStatus = 'expanded';
        this.updateProjectsDisplay();
      }
    } else if (row.treeStatus === 'expanded') {
      // Collapsing
      row.treeStatus = 'collapsed';
      this.updateProjectsDisplay();
    }
  }

  // Helper method to check if a row is loading
  isRowLoading(row: any): boolean {
    return row.treeStatus === 'loading' || this.loadingChildrenMap.has(row.id);
  }

  private loadProjectChildren(parentProject: any) {
    // Show loading state for this specific project
    const originalStatus = parentProject.treeStatus;
    parentProject.treeStatus = 'loading';
    this.loadingChildrenMap.add(parentProject.id);
    
     
    // Force change detection to ensure loading state is visible
    this.cdr.markForCheck();
    
    // Add a small delay to ensure loading state is visible
    setTimeout(() => {
      this.projectService.getProjectChildren(parentProject.id).subscribe({
        next: (children) => {
             
          // Mark children with proper hierarchy
          const childrenWithHierarchy = children.map((child: any) => ({
            ...child,
            ancestorId: parentProject.id,
            treeStatus: child.hasChildren ? 'collapsed' : 'disabled',
            childrenLoaded: false,
            children: [],
            level: (parentProject.level || 0) + 1 // Track nesting level
          }));
          
          // Sort children: folders first, then alphabetically
          childrenWithHierarchy.sort((a: any, b: any) => {
            if (a.isFolder !== b.isFolder) {
              return a.isFolder ? -1 : 1;
            }
            return a.name.localeCompare(b.name);
          });
          
          // Store children in parent and mark as loaded
          parentProject.children = childrenWithHierarchy;
          parentProject.childrenLoaded = true;
          parentProject.treeStatus = 'expanded';
          
          // Clear loading state
          this.loadingChildrenMap.delete(parentProject.id);
          
          // Add children to the main projects array
          this.insertChildrenIntoProjects(parentProject, childrenWithHierarchy);
          
          this.updateProjectsDisplay();
        },
        error: (err) => {
          console.error('Failed to load children for', parentProject.name, err);
          parentProject.treeStatus = originalStatus; // Restore original state
          this.loadingChildrenMap.delete(parentProject.id); // Clear loading state
          this.cdr.markForCheck();
          this.showToast(
            this.notificationConstants.warning as ToastStateName,
            'Error loading children',
            'Failed to load child projects'
          );
        }
      });
    }, 100); // Small delay to ensure loading state is visible
  }

  private insertChildrenIntoProjects(parent: any, children: any[]) {
    // Find the parent's index in the projects array
    const parentIndex = this.projects.findIndex(p => p.id === parent.id);
    if (parentIndex !== -1) {
      // Remove any existing children of this parent first
      this.projects = this.projects.filter(p => p.ancestorId !== parent.id);
      
      // Insert children right after the parent
      this.projects.splice(parentIndex + 1, 0, ...children);
    }
  }

  private updateProjectsDisplay() {
    // Invalidate cache by clearing hash - more efficient than array recreation
    this._lastProjectsHash = '';
    this.tempProjects = [...this.projects];
    this.setFolderProjects();
    this.cdr.markForCheck(); // Trigger change detection
  }

  public onActivate(event: any) {
    if (event.type === 'click') {
      if (this.isTreeToggleClick(event.event)) {
        return;
      }

      if (event.row) {
        this.setProject([event.row]);
      }
    }
  }

  private isTreeToggleClick(domEvent: MouseEvent): boolean {
    const target = domEvent.target as HTMLElement;
    return (
      target?.classList.contains('icon') ||
      target?.classList.contains('datatable-icon-up') ||
      target?.classList.contains('datatable-icon-down')
    );
  }

  private flattenProjects(nodes: any[], result: any[] = []): any[] {
    // Create a children map for efficient lookups (avoid repeated filtering)
    const childrenMap = new Map<string, any[]>();
    this.projects.forEach(p => {
      if ((p as any).ancestorId) {
        const ancestorId = (p as any).ancestorId;
        if (!childrenMap.has(ancestorId)) {
          childrenMap.set(ancestorId, []);
        }
        childrenMap.get(ancestorId)!.push(p);
      }
    });
    
    const flatten = (nodes: any[], result: any[]): any[] => {
      for (const node of nodes) {
        result.push(node);
        if ((node as any).treeStatus === 'expanded') {
          const children = childrenMap.get(node.id) || [];
          if (children.length > 0) {
            flatten(children, result);
          }
        }
      }
      return result;
    };
    
    return flatten(nodes, result);
  }

  private getCachedFlattenedProjects(): any[] {
    // Create a lightweight hash of projects state for cache invalidation
    const currentHash = `${this.projects.length}-${this.pageNumber}-${
      this.projects.filter(p => (p as any).treeStatus === 'expanded').length
    }`;
    
    // Only recalculate if projects structure changed
    if (this._lastProjectsHash !== currentHash) {
      this._lastProjectsHash = currentHash;
      this._cachedFlattenedProjects = this.flattenProjects(
        this.projects.filter((p) => !(p as any).ancestorId) // top-level parents
      );
    }
    
    return this._cachedFlattenedProjects;
  }

  get visibleProjects(): any[] {
    const flattened = this.getCachedFlattenedProjects();
    const start = this.pageNumber * this.pageSize;
    const end = start + this.pageSize;
    return flattened.slice(start, end);
  }

  get totalVisibleCount(): number {
    return this.getCachedFlattenedProjects().length;
  }

  public getRowClass(row: any): string {
    return row.isReader ? 'row-greyed-out' : '';
  }

  public trackByProjectId(index: number, item: any): any {
    return item.id || index;
  }

  private showToast(
    state: ToastStateName,
    title: string,
    message: string,
    disableAutoClose?: boolean,
    disableManualClose?: boolean,
    timeout?: number,
    action?: Link
  ) {
    this.toastNotificationService.showToastNotification({
      state,
      title,
      message,
      disableAutoClose,
      disableManualClose,
      timeout,
      action,
    });
  }
}
